// src/pages/user/Followers.jsx
import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaUserCircle } from 'react-icons/fa';
import { UserContext } from '../../context/userContext';
import userService from '../../services/userService';
import UserList from '../../components/user/UserList';
import Loader from '../../components/common/Loader';

const Followers = () => {
  const { id } = useParams();
  const { currentUser } = useContext(UserContext);
  const [user, setUser] = useState(null);
  const [followers, setFollowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user profile and followers
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user profile
        const userData = await userService.getUserProfile(id);
        setUser(userData);
        
        // Fetch followers
        const followersData = await userService.getFollowers(id);
        setFollowers(followersData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching followers:', err);
        setError('Failed to load followers');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  // Handle follow status changes
  const handleFollowChange = (userId, isFollowing, status) => {
    // Update local state
    setFollowers(prevFollowers => 
      prevFollowers.map(follower => 
        follower._id === userId 
          ? { ...follower, followInfo: { isFollowing, status } } 
          : follower
      )
    );
  };

  return (
    <section className="py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-8 flex items-center">
          <Link 
            to={`/profile/${id}`} 
            className="mr-4 text-gray-600 hover:text-primary transition-colors"
          >
            <FaArrowLeft size={20} />
          </Link>
          
          <h1 className="text-2xl font-bold text-gray-900">
            {user ? `${user.name}'s Followers` : 'Followers'}
          </h1>
        </div>
        
        {loading ? (
          <div className="min-h-[200px] flex items-center justify-center">
            <Loader />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        ) : followers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <h3 className="text-xl font-medium text-gray-800 mb-2">No followers yet</h3>
            {currentUser && currentUser.id === id && (
              <p className="text-gray-600">
                When people follow you, they'll show up here.
              </p>
            )}
          </div>
        ) : (
          // If UserList component is already tailwind-styled, use it directly
          // Otherwise, implement a custom followers list
          <>
            {UserList ? (
              <UserList 
                users={followers} 
                onFollowChange={handleFollowChange}
                emptyMessage="No followers yet"
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {followers.map(follower => (
                    <li key={follower._id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                            {follower.avatar ? (
                              <img 
                                src={`${process.env.REACT_APP_ASSETS_URL}/uploads/profile/${follower.avatar}`} 
                                alt={follower.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center bg-gray-200">
                                <FaUserCircle className="text-gray-400" size={24} />
                              </div>
                            )}
                          </div>
                          
                          <div className="ml-4">
                            <Link 
                              to={`/profile/${follower._id}`}
                              className="font-medium text-gray-900 hover:text-primary transition-colors"
                            >
                              {follower.name}
                            </Link>
                            {follower.bio && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                                {follower.bio}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {currentUser && currentUser.id !== follower._id && (
                          <button
                            onClick={() => handleFollowChange(
                              follower._id, 
                              !follower.followInfo?.isFollowing,
                              follower.followInfo?.isFollowing ? 'unfollowed' : 'followed'
                            )}
                            className={`px-4 py-1 text-sm font-medium rounded-full ${
                              follower.followInfo?.isFollowing
                                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                : 'bg-primary text-white hover:bg-opacity-90'
                            } transition-colors`}
                          >
                            {follower.followInfo?.isFollowing ? 'Following' : 'Follow'}
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Followers;